import { DialogTitle, GridProps, useTheme } from '@mui/material';
import { Checkbox, ContextForm, DatePicker, IFormAfterSubmit, IFormLookupFilterProps, InputOnlyField, Numeric, Select } from '@ngt/forms';
import { ValidateOn } from '@ngt/forms-core';
import { FC, useCallback, useContext } from 'react';
import { Lot, LotSaleStatuses, StageSalesStatuses } from '../api/dtos';
import StageContext from '../contexts/data/StageContext';
import LotDetailsContext from '../contexts/LotDetailsContext';
import LotSummariesContext from '../contexts/LotSummariesContext';
import StageDetailsContext from '../contexts/StageDetailsContext';
import { DollarInput, FixedHeightInputOnlyField, FormSection, IFormDialogProps, StyledButton, StyledDialog, StyledDialogActions, StyledDialogContent, StyledFormColumn, StyledFormSubmitButton } from './inputs/CommonFormElements';

const COLUMN_SIZING: GridProps['size'] = {
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12
} 



const LotForm: FC<IFormDialogProps> = (props) => {
    const theme = useTheme();
    const { refresh: refreshLotSummaries } = useContext(LotSummariesContext);
    const { refresh: refreshLotDetails } = useContext(LotDetailsContext);
    const { refresh: refreshStageDetails } = useContext(StageDetailsContext);

    const stageSalesStatus = useContext(StageContext).data?.salesStatus;

    const lotStatusFilter: IFormLookupFilterProps<Lot>['filterFunction'] = useCallback((_formState, _propertyPath, lookup) => {
        if (!lookup) {
            return undefined;
        }
    
        return {
            ...lookup,
            items: lookup.items
                ?.map(x => {
                    const disabled = !(
                        (
                            stageSalesStatus === StageSalesStatuses.Englobo &&
                            x.id == LotSaleStatuses.Englobo
                        ) || (
                            stageSalesStatus === StageSalesStatuses.Future &&
                            x.id == LotSaleStatuses.Future
                        ) || (
                            stageSalesStatus !== StageSalesStatuses.Englobo &&
                            stageSalesStatus !== StageSalesStatuses.Future &&
                            x.id !== LotSaleStatuses.Englobo &&
                            x.id !== LotSaleStatuses.Future
                        )
                    );

                    return {...x, disabled: !!disabled}
                }
            )
        }
    }, [stageSalesStatus]);

    const onAfterSubmit: IFormAfterSubmit<Lot> = useCallback(async (_newValue, _formState, _formActions) => {
        await refreshLotSummaries();
        await refreshLotDetails();
        await refreshStageDetails();
        props.onClose();
    }, [refreshLotSummaries, props.onClose])

    return (
        <ContextForm
            onAfterSubmit={onAfterSubmit}
            validateOn={ValidateOn.onChange}
            validateOnReset
        >
            <StyledDialog {...props}>
                <DialogTitle
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: "white"
                    }}
                >
                    {props.edit ? "Edit" : "Add"} Lot
                </DialogTitle>
                <StyledDialogContent>
                    <FormSection>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <InputOnlyField 
                                label="Lot No."
                                component={Numeric}
                                propertyPath={'number'}
                            />
                            <DollarInput
                                label="Feasibility Price" 
                                propertyPath={'feasibilityPrice'}
                            />
                            <DollarInput
                                label="List Price" 
                                propertyPath={'advertisedPrice'}
                            />
                            <DollarInput
                                label="Rebate" 
                                propertyPath={'approvedRebate'}
                            />
                            <InputOnlyField
                                label="Release Date" 
                                component={DatePicker}
                                propertyPath={'dateOnMarket'}
                            />
                            <InputOnlyField 
                                label="Status"
                                component={Select}
                                LookupFilterProps={{
                                    filterFunction: lotStatusFilter,
                                    subscription: {
                                        value: true
                                    }
                                }}
                                propertyPath={'saleStatus'}
                            />
                            <FixedHeightInputOnlyField
                                label="Display Home" 
                                component={Checkbox}
                                propertyPath={'displayHome'}
                            />
                            <DollarInput
                                label="Land Value for Margin Scheme" 
                                propertyPath={'landValue'}                    
                            />
                            <InputOnlyField
                                label="Land Value Date" 
                                component={DatePicker}
                                propertyPath={'landValueDate'}                    
                            />
                            <InputOnlyField
                                label="SubStage"
                                component={Select}
                                propertyPath={'subStage'}
                            />
                        </StyledFormColumn>
                    </FormSection>
                </StyledDialogContent>
                <StyledDialogActions>
                    <StyledFormSubmitButton variant="contained">Submit</StyledFormSubmitButton>
                    <StyledButton variant="contained" onClick={props.onClose}>Cancel</StyledButton>
                </StyledDialogActions>
            </StyledDialog>
        </ContextForm>
    );
}

export default LotForm;